/**
 * Internal Toolbar — chunk boundary + gate.
 *
 * This module is intentionally tiny and lives in the main bundle. The actual
 * toolbar UI is a separate `internal-tools` webpack chunk that is only fetched
 * once the gate passes — so non-internal users never download a byte of it.
 *
 * Gate (v1): a server-resolved feature flag. Morpheus is already an internal
 * ops app, so this flag is how we scope the devtools surface to
 * engineers/admins and control rollout/kill. Kept as a single predicate so the
 * whole pattern can lift into a shared package later (this is a trial run).
 */
import React, { lazy, Suspense } from 'react'
import { useSelector } from 'react-redux'
import type { primitives } from '@adc/blueprint'

interface StoreShape {
  user: primitives.AuthenticatedAuctionUser
  featureFlags?: Record<string, boolean>
}

export const INTERNAL_TOOLBAR_FLAG = 'fe.operator.enable-toolbar'

// The dynamic import is the chunk seam: webpack emits `internal-tools.js` and
// only requests it when this lazy component first renders (i.e. gate passed).
const InternalToolbar = lazy(
  () => import(/* webpackChunkName: "internal-tools" */ './InternalToolbar')
)

export const InternalToolbarLoader: React.FC = () => {
  const enabled = useSelector((s: StoreShape) =>
    Boolean(s.featureFlags?.[INTERNAL_TOOLBAR_FLAG])
  )

  if (!enabled) return null

  // No fallback: the toolbar mounts silently when its chunk arrives; there is
  // nothing meaningful to show in the sub-second gap.
  return (
    <Suspense fallback={null}>
      <InternalToolbar />
    </Suspense>
  )
}
